import React, { useLayoutEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import Content from '../../../layout/Content';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { MAINTENANCE_STAY_STATUS } from '../../../../config/constants';
import MaintenanceBusFleetSearchForm from './MaintenanceBusFleetSearchForm';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT, TIMESTAMP_FORMAT } from '../../../../config/locale';
import {
  clearMaintenancedBusFleets,
  getMaintenancedBusFleets,
  putBulkUpdateMaintenanceStay,
} from '../../../../actions';
import MaintenanceBusFleetTable from './MaintenanceBusFleetTable';

const MaintenanceBusFleet = ({
  breadcrumbs,
  dispatchGetMaintenancedBusFleets,
  dispatchClearMaintenancedBusFleets,
  dispatchPutBulkUpdateMaintenanceBusFleet,
  data,
  loading,
}) => {
  useLayoutEffect(
    () => () => dispatchClearMaintenancedBusFleets(),
    [],
  );

  const handleSearch = (formValues) => {
    const { fromDateString, toDateString, city } = formValues;
    const newFormValues = {
      fromDateString: tzNormalizeDate({ date: fromDateString, format: INT_DATE_FORMAT }),
      toDateString: tzNormalizeDate({ date: toDateString, format: INT_DATE_FORMAT }),
      cityId: city.value,
    };
    dispatchGetMaintenancedBusFleets(newFormValues);
  };

  const onHandleBulkSave = (updatedData) => {
    const updates = updatedData.map((item) => ({
      id: item.busMaintenanceStayId,
      registeredBusId: (item.registeredBusId && item.registeredBusId.value) || item.registeredBusId,
      originCityId: (item.originCityId && item.originCityId.value) || item.originCityId,
      maintenanceStayStatus: (item.maintenanceStayStatus && item.maintenanceStayStatus.value) || item.maintenanceStayStatus,
      observation: item.observation,
      estimatedArrivalDate: item.estimatedArrivalDate
        ? tzNormalizeDate({ date: item.estimatedArrivalDate, format: TIMESTAMP_FORMAT })
        : null,
      estimatedCompletionDate: item.estimatedCompletionDate
        ? tzNormalizeDate({ date: item.estimatedCompletionDate, format: TIMESTAMP_FORMAT })
        : null,
      noArrivalTime: item.noArrivalTime,
    }));

    const payload = {
      updateBusMaintenanceStayList: updates,
    };

    dispatchPutBulkUpdateMaintenanceBusFleet(payload);
  };

  const { completedCount, inProgressCount, pendingCount } = useMemo(() => data.reduce(
    (acc, item) => {
      const {
        estimatedCompletionDate,
        maintenanceStayStatus,
      } = item || {};

      if (maintenanceStayStatus === MAINTENANCE_STAY_STATUS.OPERATIONAL.value) {
        acc.completedCount += 1;
        return acc;
      }

      if (
        maintenanceStayStatus !== MAINTENANCE_STAY_STATUS.OPERATIONAL.value &&
        estimatedCompletionDate != null
      ) {
        acc.inProgressCount += 1;
        return acc;
      }

      if (
        maintenanceStayStatus !== MAINTENANCE_STAY_STATUS.OPERATIONAL.value &&
        estimatedCompletionDate == null
      ) {
        acc.pendingCount += 1;
        return acc;
      }

      acc.pendingCount += 1;
      return acc;
    },
    {
      completedCount: 0,
      inProgressCount: 0,
      pendingCount: 0,
    },
  ), [data]);

  const content = (
    <>
      <Row className="text-center mb-4">
        <Col>
          <h3>Completados</h3>
          <h2>
            <strong>{completedCount}</strong>
          </h2>
        </Col>
        <Col>
          <h3>En Progreso</h3>
          <h2>
            <strong>{inProgressCount}</strong>
          </h2>
        </Col>
        <Col>
          <h3>Pendientes</h3>
          <h2>
            <strong>{pendingCount}</strong>
          </h2>
        </Col>
      </Row>

      <MaintenanceBusFleetSearchForm
        onSubmit={handleSearch}
        initialValues={{
          fromDateString: tzNormalizeDate(),
          toDateString: tzNormalizeDate(),
        }}
        loading={loading}
      />

      <MaintenanceBusFleetTable
        onHandleBulkSave={onHandleBulkSave}
      />
    </>
  );

  return (
    <Content
      title="Flota de buses en mantenimiento"
      content={content}
      breadcrumbs={breadcrumbs}
    />
  );
};

MaintenanceBusFleet.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetMaintenancedBusFleets: PropTypes.func.isRequired,
  dispatchClearMaintenancedBusFleets: PropTypes.func.isRequired,
  dispatchPutBulkUpdateMaintenanceBusFleet: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
};

MaintenanceBusFleet.defaultProps = {
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => {
  const data =
    MechanicalMaintenanceUnit.MaintenanceBusFleet.getIn(['all', 'content', 'content']) || [];

  return {
    breadcrumbs: [
      ...MechanicalMaintenanceUnit.UnitHome.get('breadcrumbs'),
      {
        text: 'Flota de buses en mantenimiento',
        href: '',
      },
    ],
    loading: MechanicalMaintenanceUnit.MaintenanceBusFleet.getIn(['all', 'loading']),
    data,
  };
};

const mapDispatchToProps = {
  dispatchGetMaintenancedBusFleets: getMaintenancedBusFleets,
  dispatchClearMaintenancedBusFleets: clearMaintenancedBusFleets,
  dispatchPutBulkUpdateMaintenanceBusFleet: putBulkUpdateMaintenanceStay,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceBusFleet);